import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useSEO } from '../hooks/useSEO';

const HowToCleanUp: React.FC = () => {
  useSEO(
    'How to Clean Up Your Instagram Following List — Purgr',
    'A practical guide to auditing and cleaning up who you follow on Instagram. Improve your feed quality and follower ratio without risking your account.'
  );

  return (
    <PageLayout title="How to Clean Up Your Instagram Following List">
      <div className="space-y-8 text-white/70 text-sm leading-relaxed">
        <p className="text-white/80 text-base">
          Following too many accounts clutters your feed, tanks your follower ratio, and makes
          Instagram's algorithm show you content you don't care about. A periodic following audit
          keeps your account healthy and your feed relevant. Here's how to do it right.
        </p>

        {/* Why ratio matters */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-2">Why your following count matters</p>
          <p className="text-white/55">
            A high following-to-followers ratio can signal to other users (and Instagram's algorithm)
            that your account is low quality or spammy. Keeping your following list tight signals
            that you're selective about what you consume — which also improves the quality of your
            own feed, since Instagram's algorithm weights your feed based on who you actively engage with.
          </p>
        </div>

        {/* Strategy */}
        <div>
          <p className="text-white/90 font-bold text-base mb-4">The cleanup strategy</p>
          <div className="space-y-4">
            {[
              {
                n: '1',
                icon: '📥',
                title: 'Start with your non-followers list',
                body: 'Use Purgr to generate your "not following back" list. These are the highest-priority candidates for unfollowing — accounts that got a follow from you but never followed back.',
              },
              {
                n: '2',
                icon: '🔍',
                title: 'Review before unfollowing',
                body: "Not every non-follower should be unfollowed. Keep accounts that add value to your feed — news outlets, brands you love, creators whose content you enjoy. The goal is quality, not just ratio.",
              },
              {
                n: '3',
                icon: '📋',
                title: 'Save your list',
                body: "Use Purgr's \"Copy to clipboard\" or \"Download CSV\" buttons to export your non-followers list. Work through it systematically rather than trying to do everything at once.",
              },
              {
                n: '4',
                icon: '⏱️',
                title: 'Unfollow in small batches',
                body: "Instagram limits how many accounts you can unfollow in a short period. A safe pace is 50–100 unfollows per day, with breaks in between. Exceeding this can trigger a temporary unfollow block or, in rare cases, an account action.",
              },
              {
                n: '5',
                icon: '🔄',
                title: 'Repeat every few months',
                body: 'Following lists grow organically over time. Set a reminder to run a Purgr audit every 3–6 months to keep your list clean.',
              },
            ].map(({ n, icon, title, body }) => (
              <div key={n} className="glass-card p-5 flex gap-4">
                <div className="w-8 h-8 rounded-full ig-gradient-bg flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">
                  {n}
                </div>
                <div>
                  <p className="text-white/90 font-semibold mb-1">{icon} {title}</p>
                  <p className="text-white/55">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Instagram limits */}
        <div className="glass-card p-5 border border-yellow-500/20">
          <p className="text-white/90 font-semibold mb-2">⚠️ Instagram rate limits</p>
          <ul className="space-y-2 text-white/55">
            <li>• Instagram imposes hourly and daily limits on unfollowing. The exact numbers change, but staying under 100 unfollows per day is generally safe.</li>
            <li>• If you hit a limit, Instagram will show a "Try again later" message. Stop and wait a few hours before continuing.</li>
            <li>• Never use bots or automation tools to unfollow — they violate Instagram's Terms of Service and can result in a permanent ban.</li>
          </ul>
        </div>

        {/* Bonus: other accounts to consider */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-2">Other accounts worth auditing</p>
          <p className="text-white/55 mb-3">
            Beyond non-followers, consider unfollowing:
          </p>
          <ul className="space-y-2 text-white/55">
            <li>• <strong className="text-white/70">Ghost accounts</strong> — accounts with no posts or that haven't posted in years.</li>
            <li>• <strong className="text-white/70">Irrelevant accounts</strong> — old interests, brands you no longer care about, accounts from people you've lost touch with.</li>
            <li>• <strong className="text-white/70">Inactive mutual follows</strong> — accounts you follow and that follow you back, but whose content you never engage with. Purgr's "Mutuals" tab can help you identify these.</li>
          </ul>
        </div>

        {/* CTA */}
        <div className="rounded-xl p-px ig-gradient-bg">
          <div className="rounded-xl bg-[#0D0D0D] px-6 py-6 text-center">
            <p className="text-white font-bold text-base mb-1">Start your following audit</p>
            <p className="text-white/50 text-sm mb-4">Get your non-followers list in seconds — free, private, no login needed.</p>
            <Link
              to="/"
              className="btn-gradient inline-block text-white font-bold px-8 py-3 rounded-full"
            >
              Try Purgr Free →
            </Link>
          </div>
        </div>

        {/* Related */}
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Related guides</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/how-to-download-instagram-data" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to download your Instagram data →</p>
            </Link>
            <Link to="/how-to-see-who-doesnt-follow-you-back" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to see who doesn't follow you back →</p>
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default HowToCleanUp;
